/**
 * Domain-level sending use-case. `sendingPurpose` on a `domains` row is
 * the product source of truth. Every send row snapshots the admitted
 * purpose as `messageClass` so unsubscribe / suppression cannot drift.
 *
 * Transactional purpose = event-triggered automation mail (receipts,
 * resets): no unsubscribe link required, merely-unsubscribed contacts
 * still receive it (hard suppressions always enforced). Marketing keeps
 * the full consent regime. Absent / unrecognized values resolve to
 * marketing so CAN-SPAM stays on unless a domain is explicitly
 * transactional.
 *
 * Every send path admits through `admitSendingPurpose` — do not
 * re-derive the surface matrix at the call site.
 */

import { z } from 'zod'
import {
  type DomainSendabilitySource,
  isDomainSendable,
} from '@/lib/domains/sendability'
import { resolveSendingPurpose } from '@/lib/domains/sending-purpose-value'

export {
  DEFAULT_SENDING_PURPOSE,
  resolveSendingPurpose,
} from '@/lib/domains/sending-purpose-value'

const SENDING_PURPOSES = ['marketing', 'transactional'] as const

export const SENDING_PURPOSE_SCHEMA = z.enum(SENDING_PURPOSES)

export type SendingPurpose = z.infer<typeof SENDING_PURPOSE_SCHEMA>

export const SEND_SURFACES = [
  'api_direct',
  'campaign',
  'automation',
  'audience_run',
  'inbox_placement',
  'test',
] as const

export type SendSurface = (typeof SEND_SURFACES)[number]

const TRANSACTIONAL_ALLOWED_SURFACES: ReadonlySet<SendSurface> = new Set([
  'automation',
  'test',
])

export type SendingPurposeSource = {
  sendingPurpose?: string | null
}

export function isSendingPurposeAllowedForSurface(
  purpose: SendingPurpose,
  surface: SendSurface
): boolean {
  if (purpose === 'marketing') {
    return true
  }
  return TRANSACTIONAL_ALLOWED_SURFACES.has(surface)
}

export type SendingPurposeSurfaceResult =
  | { ok: true }
  | { ok: false; message: string }

const TRANSACTIONAL_SURFACE_REJECTION =
  'Transactional domains send via triggered automations (or a test send). Use a marketing domain for campaigns and audience sends.'

export function assertSendingPurposeForSurface(
  purpose: SendingPurpose,
  surface: SendSurface
): SendingPurposeSurfaceResult {
  if (isSendingPurposeAllowedForSurface(purpose, surface)) {
    return { ok: true }
  }
  return {
    ok: false,
    message: TRANSACTIONAL_SURFACE_REJECTION,
  }
}

export type AdmitSendingPurposeResult =
  | { ok: true; purpose: SendingPurpose }
  | {
      ok: false
      code: 'DOMAIN_PURPOSE_NOT_ALLOWED'
      purpose: SendingPurpose
      message: string
    }

/**
 * Single admission gate for every send surface.
 *
 * Resolves purpose from the domain (missing → marketing) and rejects
 * transactional on marketing-only surfaces.
 */
export function admitSendingPurpose(args: {
  domain: SendingPurposeSource | null | undefined
  surface: SendSurface
}): AdmitSendingPurposeResult {
  const purpose = resolveSendingPurpose(args.domain?.sendingPurpose)
  const surfaceCheck = assertSendingPurposeForSurface(purpose, args.surface)
  if (!surfaceCheck.ok) {
    return {
      ok: false,
      code: 'DOMAIN_PURPOSE_NOT_ALLOWED',
      purpose,
      message: surfaceCheck.message,
    }
  }
  return { ok: true, purpose }
}

/** Verified + sending-enabled + marketing. Used by automation pickers/backfill. */
export function isMarketingSendableDomain(
  domain: DomainSendabilitySource & SendingPurposeSource
): boolean {
  return (
    isDomainSendable(domain) &&
    resolveSendingPurpose(domain.sendingPurpose) === 'marketing'
  )
}

/**
 * Shared campaign orchestrator surface. Inbox-placement and audience/all
 * (plus warmup / smart send) are marketing-only. Inline `to` is the
 * Resend-style API-direct path (also marketing-only — transactional mail
 * goes through triggered automations).
 */
export function resolveCampaignSendSurface(input: {
  purpose?: 'inbox_placement'
  recipientSource: 'audience' | 'all' | 'manual'
  gradualSend?: unknown
  /** A request's on/off, or a workflow's `SmartSendConfig`; `false` is off. */
  smartSend?: unknown
}): SendSurface {
  if (input.purpose === 'inbox_placement') {
    return 'inbox_placement'
  }
  if (
    input.recipientSource === 'audience' ||
    input.recipientSource === 'all' ||
    input.gradualSend != null ||
    (input.smartSend != null && input.smartSend !== false)
  ) {
    return 'campaign'
  }
  return 'api_direct'
}
